import React, { useEffect, useState } from 'react';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { ButtonSpinner } from '../common/Loader';
import { parseError } from '../../utils/errors';
import { formatBps, pctToBps, bpsToPctStr } from '../../utils/format';
import { sendTx } from '../../utils/txHelpers';
import { Alert } from '../common/StatBox';

export const FeeManagement = () => {
  const { viewsR, adminW } = useContracts();
  const notify = useNotify();

  const [fees, setFees] = useState(null);
  const [busy, setBusy] = useState(null);

  // Fee-split inputs (%)
  const [team, setTeam] = useState('');
  const [liq, setLiq]   = useState('');
  const [pool, setPool] = useState('');

  // Individual fees (%)
  const [adminFee, setAdminFee]     = useState('');
  const [levelFee, setLevelFee]     = useState('');
  const [expenseFee, setExpenseFee] = useState('');

  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!viewsR) return;
      try {
        const f = await viewsR.getFees();
        if (cancelled) return;
        setFees(f);
        // Pre-fill inputs with current values for convenience
        setTeam(bpsToPctStr(f.teamFee));
        setLiq(bpsToPctStr(f.liquidityFee));
        setPool(bpsToPctStr(f.rankPoolTotal));
        setAdminFee(bpsToPctStr(f.adminWithdraw));
        setLevelFee(bpsToPctStr(f.levelWithdraw));
        setExpenseFee(bpsToPctStr(f.expense));
      } catch (e) {}
    };
    load();
    return () => { cancelled = true; };
  }, [viewsR, reload]);

  // Compute split total for live feedback
  const splitTotal = (Number(team) || 0) + (Number(liq) || 0) + (Number(pool) || 0);
  const splitOk = Math.abs(splitTotal - 100) < 0.001;

  const runTx = async (label, method, args) => {
    if (!adminW) { notify.error('Wallet not ready'); return; }
    setBusy(label);
    try {
      await sendTx(adminW, method, args, { notify, label });
      setReload((r) => r + 1);
    } catch (e) {
      notify.error(parseError(e));
    } finally {
      setBusy(null);
    }
  };

  const updateSplits = () => {
    const t = pctToBps(team), l = pctToBps(liq), p = pctToBps(pool);
    if (t == null || l == null || p == null) { notify.error('Invalid values'); return; }
    if (t + l + p !== 10000) {
      notify.error(`Splits must total exactly 100% (currently ${((t + l + p) / 100).toFixed(2)}%)`);
      return;
    }
    runTx('splits', 'updateFeeSplits', [t, l, p]);
  };

  const updateSingleFee = (label, method, val) => {
    const bps = pctToBps(val);
    if (bps == null) { notify.error('Invalid percentage'); return; }
    if (bps > 5000) { notify.error('Fee cannot exceed 50%'); return; }
    runTx(label, method, [bps]);
  };

  return (
    <div>
      {/* Current fees display */}
      <div className="card mb-24">
        <div className="section-title">Current Fees</div>
        {fees && (
          <div className="kv-list">
            <div className="kv-row"><span className="kv-key">Team Wallet</span><span className="kv-val">{formatBps(fees.teamFee)}</span></div>
            <div className="kv-row"><span className="kv-key">Liquidity Wallet</span><span className="kv-val">{formatBps(fees.liquidityFee)}</span></div>
            <div className="kv-row"><span className="kv-key">Rank Pool Total</span><span className="kv-val">{formatBps(fees.rankPoolTotal)}</span></div>
            <div className="kv-row"><span className="kv-key">Admin Withdraw Fee</span><span className="kv-val">{formatBps(fees.adminWithdraw)}</span></div>
            <div className="kv-row"><span className="kv-key">Level Withdraw Fee</span><span className="kv-val">{formatBps(fees.levelWithdraw)}</span></div>
            <div className="kv-row"><span className="kv-key">Expense Skim</span><span className="kv-val">{formatBps(fees.expense)}</span></div>
          </div>
        )}
      </div>

      {/* Fee splits (must total 100%) */}
      <div className="card mb-24">
        <div className="section-title">Investment Fee Splits (must total 100%)</div>
        <div className="admin-form-grid">
          <div className="input-group">
            <label className="input-label">Team (%)</label>
            <input className="input" type="number" step="0.01" placeholder="20" value={team} onChange={(e) => setTeam(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Liquidity (%)</label>
            <input className="input" type="number" step="0.01" placeholder="76" value={liq} onChange={(e) => setLiq(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Rank Pool (%)</label>
            <input className="input" type="number" step="0.01" placeholder="4" value={pool} onChange={(e) => setPool(e.target.value)} />
          </div>
        </div>
        <div style={{ marginBottom: 12, fontSize: 13, color: splitOk ? 'var(--neon-green)' : 'var(--neon-red)', fontWeight: 700 }}>
          Total: {splitTotal.toFixed(2)}% {splitOk ? '✓' : '(must be 100%)'}
        </div>
        <button className="btn btn-primary" disabled={busy === 'splits' || !splitOk} onClick={updateSplits}>
          {busy === 'splits' && <ButtonSpinner />}Update Splits
        </button>
      </div>

      {/* Individual fees */}
      <div className="card mb-24">
        <div className="section-title">Individual Fees (max 50%)</div>
        <Alert type="info">
          Admin withdraw fee is now paid in <b>ZENOVA</b> (not USDC) under normal claim mode.
        </Alert>
        <div className="admin-form-grid">
          <div>
            <div className="input-group">
              <label className="input-label">Admin Withdraw Fee (%)</label>
              <input className="input" type="number" step="0.01" value={adminFee} onChange={(e) => setAdminFee(e.target.value)} />
            </div>
            <button className="btn btn-secondary btn-sm" disabled={busy === 'admin' || !adminFee}
              onClick={() => updateSingleFee('admin', 'updateAdminWithdrawFee', adminFee)}>
              {busy === 'admin' && <ButtonSpinner />}Set
            </button>
          </div>
          <div>
            <div className="input-group">
              <label className="input-label">Level Withdraw Fee (%)</label>
              <input className="input" type="number" step="0.01" value={levelFee} onChange={(e) => setLevelFee(e.target.value)} />
            </div>
            <button className="btn btn-secondary btn-sm" disabled={busy === 'level' || !levelFee}
              onClick={() => updateSingleFee('level', 'updateLevelWithdrawFee', levelFee)}>
              {busy === 'level' && <ButtonSpinner />}Set
            </button>
          </div>
          <div>
            <div className="input-group">
              <label className="input-label">Expense Skim (%)</label>
              <input className="input" type="number" step="0.01" value={expenseFee} onChange={(e) => setExpenseFee(e.target.value)} />
            </div>
            <button className="btn btn-secondary btn-sm" disabled={busy === 'expense' || !expenseFee}
              onClick={() => updateSingleFee('expense', 'updateExpenseFee', expenseFee)}>
              {busy === 'expense' && <ButtonSpinner />}Set
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
